import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wallet, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, XCircle, Lock, Building2, MapPin, Banknote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentPage = () => {
  const { user, token, isAuthenticated, isAdmin, refreshUser } = useAuth();
  const [payments, setPayments] = useState([]);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawalType, setWithdrawalType] = useState("bank"); // "bank" or "local"
  const [loading, setLoading] = useState(false);
  
  const SERVICE_FEE = 0.10; // 10%

  useEffect(() => {
    if (isAuthenticated) {
      fetchPayments();
      fetchWithdrawalHistory();
      if (isAdmin) {
        fetchPendingPayments();
      }
    }
  }, [isAuthenticated, isAdmin, token]);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      console.error("Error fetching payments:", error);
    }
  };
  
  const fetchWithdrawalHistory = async () => {
    try {
      const response = await axios.get(`${API}/payments/withdrawal-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawalHistory(response.data);
    } catch (error) {
      console.error("Error fetching withdrawal history:", error);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPayments(response.data);
    } catch (error) {
      console.error("Error fetching pending payments:", error);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    const amount = parseFloat(withdrawAmount);
    
    if (amount <= 0) {
      toast.error("El monto debe ser mayor a 0");
      return;
    }
    
    if (amount > (user?.balance || 0)) {
      toast.error("Saldo insuficiente");
      return;
    }

    // For bank withdrawals, check if user has bank info
    if (withdrawalType === "bank" && (!user?.bank_name || !user?.bank_account)) {
      toast.error("Debes configurar tu cuenta bancaria o selecciona 'Cobrar Local'");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/payments/request-withdrawal`,
        { amount, withdrawal_type: withdrawalType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (withdrawalType === "local") {
        toast.success(`Solicitud de COBRO LOCAL enviada. El admin te contactará.`);
      } else {
        toast.success(`Solicitud enviada. Recibirás RD$${response.data.net_amount?.toFixed(2)} (10% comisión aplicada)`);
      }
      setWithdrawAmount("");
      await refreshUser();
      fetchPayments();
      fetchWithdrawalHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar el retiro");
    } finally {
      setLoading(false);
    }
  };
  
  const calculateWithdrawalPreview = () => {
    const amount = parseFloat(withdrawAmount) || 0;
    const commission = amount * SERVICE_FEE;
    const net = amount - commission;
    return { gross: amount, commission, net };
  };

  const handleUpdatePaymentStatus = async (paymentId, status) => {
    try {
      await axios.put(
        `${API}/payments/${paymentId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Pago ${status === "completed" ? "completado" : "rechazado"}`);
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar el pago");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-[#22C55E]" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Completado";
      case "rejected":
        return "Rechazado";
      default:
        return "Pendiente";
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pb-20 p-4 animate-fadeInUp flex items-center justify-center" data-testid="payment-page-auth">
        <Card className="bg-[#121212] border-white/10 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Lock className="w-12 h-12 text-[#D946EF] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Acceso Restringido</h2>
            <p className="text-gray-400">Inicia sesión para acceder a los pagos</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 p-4 animate-fadeInUp" data-testid="payment-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold gradient-text">Pagos</h1>

        {/* User Balance Card (for regular users) */}
        {!isAdmin && user && (
          <>
            <Card className="bg-gradient-to-br from-[#22C55E]/20 to-[#10B981]/20 border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Puntos Disponibles</p>
                    <p className="text-4xl font-bold text-[#D946EF]">
                      {Math.round(user.balance || 0).toLocaleString()} pts
                    </p>
                  </div>
                  <div className="w-16 h-16 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                    <Wallet className="w-8 h-8 text-[#22C55E]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Info */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#D946EF]" />
                  Cuenta de Retiro
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Banco:</span>
                  <span className={`font-medium ${!user.bank_name ? 'text-gray-500 italic' : ''}`}>
                    {user.bank_name || 'No configurada'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Cuenta:</span>
                  <span className={`mono ${!user.bank_account ? 'text-gray-500 italic' : ''}`}>
                    {user.bank_account || 'No configurada'}
                  </span>
                </div>
                {(!user.bank_name || !user.bank_account) && (
                  <p className="text-xs text-[#D946EF] mt-2">
                    💡 Sin cuenta bancaria? Usa "Cobrar Local" para retirar en persona.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Withdraw Form */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowUpRight className="w-5 h-5 text-[#D946EF]" />
                  Solicitar Retiro
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleWithdraw} className="space-y-4">
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-4">
                    <p className="text-sm text-yellow-500">
                      ⚠️ <strong>Comisión del 10%</strong> será aplicada al retiro
                    </p>
                  </div>
                  
                  {/* Withdrawal Type Selection */}
                  <div className="space-y-2">
                    <Label>Método de Cobro</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setWithdrawalType("bank")}
                        data-testid="bank-cashout-btn"
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          withdrawalType === "bank"
                            ? "border-[#22C55E] bg-[#22C55E]/10"
                            : "border-white/10 bg-[#1E1E1E] hover:border-white/20"
                        }`}
                      >
                        <Banknote className="w-6 h-6 mx-auto mb-1" />
                        <span className="text-sm font-medium">Banco</span>
                        <p className="text-xs text-gray-400">Transferencia</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setWithdrawalType("local")}
                        data-testid="local-cashout-btn"
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          withdrawalType === "local"
                            ? "border-[#D946EF] bg-[#D946EF]/10"
                            : "border-white/10 bg-[#1E1E1E] hover:border-white/20"
                        }`}
                      >
                        <MapPin className="w-6 h-6 mx-auto mb-1" />
                        <span className="text-sm font-medium">Cobrar Local</span>
                        <p className="text-xs text-gray-400">En persona</p>
                      </button>
                    </div>
                    {withdrawalType === "local" && (
                      <p className="text-xs text-[#D946EF] mt-2">
                        El admin te contactará para coordinar el cobro en persona
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="amount">Monto a Retirar (puntos)</Label>
                    <Input
                      id="amount"
                      data-testid="withdraw-amount"
                      type="number"
                      placeholder="0.00"
                      min="1"
                      step="0.01"
                      className="bg-[#1E1E1E] border-white/10 text-2xl h-14"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      required
                    />
                    <p className="text-xs text-gray-400">
                      Máximo disponible: {Math.round(user.balance || 0).toLocaleString()} pts
                    </p>
                  </div>
                  
                  {/* Commission Preview */}
                  {withdrawAmount && parseFloat(withdrawAmount) > 0 && (
                    <div className="p-4 bg-[#1E1E1E] rounded-lg space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Puntos a retirar:</span>
                        <span>{Math.round(calculateWithdrawalPreview().gross).toLocaleString()} pts</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Comisión (10%):</span>
                        <span className="text-red-400">-{Math.round(calculateWithdrawalPreview().commission).toLocaleString()} pts</span>
                      </div>
                      <hr className="border-white/10" />
                      <div className="flex justify-between font-bold">
                        <span>Recibirás:</span>
                        <span className="text-[#22C55E]">RD${calculateWithdrawalPreview().net.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    type="submit"
                    data-testid="withdraw-submit"
                    disabled={loading || !withdrawAmount}
                    className={`w-full h-12 text-lg font-bold ${
                      withdrawalType === "local" 
                        ? "bg-[#D946EF] hover:bg-[#D946EF]/80 text-white"
                        : "bg-[#22C55E] hover:bg-[#22C55E]/80 text-black"
                    }`}
                  >
                    {loading ? "Procesando..." : withdrawalType === "local" ? "Solicitar Cobro Local" : "Solicitar Retiro Bancario"}
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            {/* Withdrawal History */}
            {withdrawalHistory.length > 0 && (
              <Card className="bg-[#121212] border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#D946EF]" />
                    Historial de Retiros
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {withdrawalHistory.map((withdrawal) => (
                      <div
                        key={withdrawal.id}
                        className={`flex items-center justify-between p-4 bg-[#1E1E1E] rounded-lg ${
                          withdrawal.withdrawal_type === "local" ? "border-l-4 border-[#D946EF]" : ""
                        }`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            {withdrawal.status === "completed" && <CheckCircle className="w-4 h-4 text-[#22C55E]" />}
                            {withdrawal.status === "pending" && <Clock className="w-4 h-4 text-yellow-500" />}
                            {withdrawal.status === "rejected" && <XCircle className="w-4 h-4 text-red-500" />}
                            <span className="text-sm text-gray-400">
                              {new Date(withdrawal.created_at).toLocaleDateString("es-DO")}
                            </span>
                            {withdrawal.withdrawal_type === "local" && (
                              <span className="text-xs px-2 py-0.5 rounded bg-[#D946EF]/20 text-[#D946EF]">LOCAL</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            Solicitado: RD${withdrawal.gross_amount} | Comisión: RD${withdrawal.commission?.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#22C55E]">RD${withdrawal.net_amount?.toFixed(2)}</p>
                          <p className="text-xs capitalize text-gray-400">
                            {withdrawal.status === "completed" ? "Completado" : 
                             withdrawal.status === "pending" ? "Pendiente" : "Rechazado"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Admin: Pending Payments */}
        {isAdmin && (
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                Pagos Pendientes ({pendingPayments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingPayments.length > 0 ? (
                <div className="space-y-3">
                  {pendingPayments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-4 bg-[#1E1E1E] rounded-lg"
                      data-testid={`pending-payment-${payment.id}`}
                    >
                      <div>
                        <p className="font-medium">{payment.user_name}</p>
                        <p className="text-sm text-gray-400">
                          {payment.bank_name} - {payment.bank_account}
                        </p>
                        <p className="text-lg font-bold text-[#22C55E]">
                          RD${payment.amount.toFixed(2)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleUpdatePaymentStatus(payment.id, "completed")}
                          size="sm"
                          className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black"
                          data-testid={`approve-payment-${payment.id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleUpdatePaymentStatus(payment.id, "rejected")}
                          size="sm"
                          variant="destructive"
                          data-testid={`reject-payment-${payment.id}`}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 py-4">No hay pagos pendientes</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment History */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowDownLeft className="w-5 h-5 text-[#D946EF]" />
              Historial de Pagos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length > 0 ? (
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded-lg"
                    data-testid={`payment-history-${payment.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(payment.status)}
                      <div>
                        <p className="font-medium">
                          {payment.payment_type === "withdrawal" ? "Retiro" : "Pago Artista"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(payment.created_at).toLocaleDateString("es-DO")}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${
                        payment.status === "completed" ? "text-[#22C55E]" : 
                        payment.status === "rejected" ? "text-red-500" : "text-yellow-500"
                      }`}>
                        RD${payment.amount.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-400">{getStatusText(payment.status)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-400 py-4">No hay historial de pagos</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentPage;
